import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../ConfirmModal.css';

/**
 * SendConfirmModal — final safety-net before an outbound send when the
 * recipient already has history. Displayed in two situations:
 *
 *   1) Re-send guard (intro/second intro): the exact address has already
 *      received an intro or follow-up. Variant `danger` (red).
 *   2) Follow-up sanity check: the user is composing a follow-up and there
 *      is prior conversation on this contact. Variant `warning` (amber).
 *
 * The dialog always shows *what was actually last sent* (subject + date)
 * plus the total count of past sends, so the user can decide with real
 * context and does not blindly duplicate an intro or a follow-up step.
 */

const formatDate = (ms) => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString('en-US', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_e) {
    return '';
  }
};

const relative = (ms) => {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const day = 86400000;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < day) return `${Math.round(diff / 3600000)} h ago`;
  if (diff < 30 * day) return `${Math.round(diff / day)} days ago`;
  if (diff < 365 * day) return `${Math.round(diff / (30 * day))} months ago`;
  return `${Math.round(diff / (365 * day))} years ago`;
};

export default function SendConfirmModal({
  isOpen,
  variant = 'warning', // 'warning' (follow-up sanity) | 'danger' (already sent to exact address)
  recipient,           // string, the concrete address about to be mailed
  sentCount = 0,       // total prior sends we know about
  lastSent = null,     // { subject, ms } — legacy single-message shape (backward compat)
  sentMessages = null, // [{ id, subject, ms, bodyText, from }] newest-first (preferred)
  title,
  message,
  confirmText = 'Send anyway',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
}) {
  const btnRef = useRef(null);
  // Accordion-State pro Modal-Öffnung: die neueste Nachricht ist per Default
  // aufgeklappt (der User will primär DIE sehen), ältere sind eingeklappt.
  // Wir keyen den State auf die IDs der Messages, damit der Zustand beim
  // erneuten Öffnen sauber resettet wird.
  const [expandedId, setExpandedId] = useState(null);

  const messages = Array.isArray(sentMessages) && sentMessages.length > 0
    ? sentMessages
    : (lastSent ? [lastSent] : []);

  useEffect(() => {
    if (!isOpen) return undefined;
    // Beim Öffnen des Modals die neueste Nachricht aufklappen.
    if (messages.length > 0) {
      setExpandedId(messages[0].id || 'msg-0');
    }
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel && onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onCancel, messages.length]);

  if (!isOpen) return null;

  const isDanger = variant === 'danger';
  const resolvedTitle = title || (isDanger
    ? 'Address already emailed'
    : 'Prior conversation on this contact');
  const resolvedMessage = message || (isDanger
    ? `An email has already been sent to ${recipient}. Are you sure you want to email this exact address again?`
    : `You have already exchanged mails with this contact. Please review the last message before sending the next step to ${recipient}.`);

  return createPortal(
    <div className="bc-confirm-overlay" onClick={onCancel}>
      <div
        className="bc-confirm-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 460, maxWidth: '94vw' }}
      >
        <div className={`bc-confirm-icon bc-confirm-icon--${isDanger ? 'danger' : 'warning'}`}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        <h3 className="bc-confirm-title">{resolvedTitle}</h3>
        <p className="bc-confirm-message" style={{ maxWidth: 380 }}>{resolvedMessage}</p>

        {(recipient || sentCount > 0 || messages.length > 0) && (
          <div style={styles.previewCard}>
            {recipient && (
              <div style={styles.recipientRow}>
                <span style={styles.metaLabel}>To</span>
                <code style={styles.recipient}>{recipient}</code>
              </div>
            )}
            {sentCount > 0 && (
              <div style={styles.countRow}>
                <span style={styles.countBadge}>{sentCount}</span>
                <span style={styles.countText}>
                  previous {sentCount === 1 ? 'email' : 'emails'} sent to this contact
                </span>
              </div>
            )}

            {messages.length > 0 && (
              <div style={styles.messageList}>
                {messages.map((msg, idx) => {
                  const key = msg.id || `msg-${idx}`;
                  const isFirst = idx === 0;
                  const isExpanded = expandedId === key;
                  const body = (msg.bodyText || '').trim();
                  const preview = body ? body.slice(0, 140) : '';
                  const hasMore = body.length > 140;
                  return (
                    <div
                      key={key}
                      style={{
                        ...styles.messageCard,
                        ...(isExpanded ? styles.messageCardExpanded : {}),
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setExpandedId(isExpanded ? null : key)}
                        style={styles.messageHead}
                        aria-expanded={isExpanded}
                      >
                        <div style={styles.messageHeadRow}>
                          <i className="fa fa-paper-plane" style={styles.messageIcon} aria-hidden />
                          <span style={styles.messageLabel}>
                            {isFirst ? 'Last message' : `Earlier · ${idx + 1}`}
                          </span>
                          {msg.ms ? (
                            <span style={styles.messageAgo}>{relative(msg.ms)}</span>
                          ) : null}
                          <i
                            className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}
                            style={styles.messageChevron}
                            aria-hidden
                          />
                        </div>
                        {msg.subject && (
                          <div style={styles.messageSubject} title={msg.subject}>
                            {msg.subject || '(no subject)'}
                          </div>
                        )}
                        {!isExpanded && preview && (
                          <div style={styles.messagePreview}>
                            {preview}{hasMore ? '…' : ''}
                          </div>
                        )}
                      </button>

                      {isExpanded && (
                        <div style={styles.messageBody}>
                          {(msg.from || msg.to || msg.ms) && (
                            <div style={styles.messageMetaGrid}>
                              {msg.from && (
                                <>
                                  <span style={styles.messageMetaLabel}>From</span>
                                  <code style={styles.messageMetaValue}>{msg.from}</code>
                                </>
                              )}
                              {msg.to && (
                                <>
                                  <span style={styles.messageMetaLabel}>To</span>
                                  <code style={styles.messageMetaValue}>{msg.to}</code>
                                </>
                              )}
                              {msg.ms ? (
                                <>
                                  <span style={styles.messageMetaLabel}>Sent</span>
                                  <span style={styles.messageMetaValue}>{formatDate(msg.ms)}</span>
                                </>
                              ) : null}
                            </div>
                          )}
                          {body ? (
                            <div style={styles.messageBodyText}>{body}</div>
                          ) : (
                            <div style={styles.messageBodyEmpty}>(no readable body content)</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="bc-confirm-actions">
          <button className="bc-confirm-btn bc-confirm-btn--cancel" onClick={onCancel}>
            {cancelText}
          </button>
          <button
            ref={btnRef}
            className={`bc-confirm-btn bc-confirm-btn--${isDanger ? 'danger' : 'warning'}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const styles = {
  previewCard: {
    width: '100%',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '12px 14px',
    marginBottom: 20,
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  recipientRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  metaLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#94a3b8',
    flexShrink: 0,
  },
  recipient: {
    fontFamily: 'source-code-pro, Menlo, monospace',
    fontSize: 12,
    color: 'var(--darkblue, #2a6776)',
    background: 'var(--transparentblue, rgba(104,151,183,0.08))',
    padding: '2px 8px',
    borderRadius: 6,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  countRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 22,
    height: 22,
    padding: '0 6px',
    borderRadius: 999,
    background: '#f59e0b',
    color: 'white',
    fontSize: 11,
    fontWeight: 700,
  },
  countText: {
    fontSize: 12,
    color: '#475569',
  },
  messageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    maxHeight: 320,
    overflowY: 'auto',
    paddingRight: 2,
  },
  messageCard: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    overflow: 'hidden',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
  },
  messageCardExpanded: {
    borderColor: 'rgba(104,151,183,0.35)',
    boxShadow: '0 4px 12px rgba(104,151,183,0.10)',
  },
  messageHead: {
    display: 'block',
    width: '100%',
    background: 'transparent',
    border: 'none',
    padding: '10px 12px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  messageHeadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  messageIcon: {
    fontSize: 11,
    color: '#6366f1',
    flexShrink: 0,
  },
  messageLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#64748b',
    flex: 1,
  },
  messageAgo: {
    fontSize: 10,
    fontWeight: 600,
    color: '#94a3b8',
    background: '#f1f5f9',
    padding: '1px 8px',
    borderRadius: 999,
  },
  messageChevron: {
    fontSize: 10,
    color: '#94a3b8',
    marginLeft: 4,
  },
  messageSubject: {
    fontSize: 13,
    fontWeight: 600,
    color: '#0f172a',
    lineHeight: 1.35,
    marginTop: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  messagePreview: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 4,
    lineHeight: 1.45,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  messageBody: {
    padding: '10px 12px 12px',
    borderTop: '1px solid #f1f5f9',
    background: '#fafbfc',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  messageMetaGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '2px 10px',
    alignItems: 'baseline',
  },
  messageMetaLabel: {
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#94a3b8',
  },
  messageMetaValue: {
    fontSize: 11,
    color: '#334155',
    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  messageBodyText: {
    fontSize: 12.5,
    color: '#0f172a',
    lineHeight: 1.55,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 220,
    overflowY: 'auto',
    padding: '6px 8px',
    background: 'white',
    borderRadius: 8,
    border: '1px solid #eef2f6',
  },
  messageBodyEmpty: {
    fontSize: 11.5,
    color: '#94a3b8',
    fontStyle: 'italic',
  },
};
